"use client";

import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { User, Mail, Phone, Send, Loader2, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";

interface ContactLandingFormInputs {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  msg: string;
}

export default function ContactLandingForm() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactLandingFormInputs>();
  const router = useRouter();

  const onSubmit: SubmitHandler<ContactLandingFormInputs> = async (formData) => {
    setIsLoading(true);
    try {
        const res = await fetch("/api/contact", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                name: `${formData.firstName} ${formData.lastName}`,
                email: formData.email,
                phone: formData.phone,
                message: formData.msg,
            }),
        });
        
        if (res.ok) {
            router.push("/kapcsolatfelvetel/visszaigazolas");
            toast.success("Üzenetét sikeresen elküldtük!");
            reset();
        } else {
            toast.error("Hiba történt a küldés során, kérjük próbálja újra!");
        }
    } catch (error) {
        toast.error("Hálózati hiba történt. Kérjük, ellenőrizze kapcsolatát!");
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-bold flex items-center gap-2 text-foreground/80">
            <User className="w-4 h-4 text-primary" /> Vezetéknév*
          </label>
          <input
            type="text"
            className={`w-full rounded-xl border bg-background px-4 py-3 outline-hidden focus:ring-2 transition-all shadow-xs ${
              errors.lastName ? "border-red-500 focus:ring-red-500/10" : "border-border focus:ring-primary/20 focus:border-primary"
            }`}
            placeholder="Kovács"
            {...register("lastName", { required: true })}
          />
          {errors.lastName && (
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">
              A vezetéknév kötelező
            </p>
          )}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-bold flex items-center gap-2 text-foreground/80">
            <User className="w-4 h-4 text-primary" /> Keresztnév*
          </label>
          <input
            type="text"
            className={`w-full rounded-xl border bg-background px-4 py-3 outline-hidden focus:ring-2 transition-all shadow-xs ${
              errors.firstName ? "border-red-500 focus:ring-red-500/10" : "border-border focus:ring-primary/20 focus:border-primary"
            }`}
            placeholder="Anna"
            {...register("firstName", { required: true })}
          />
          {errors.firstName && (
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">
              A keresztnév kötelező
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-bold flex items-center gap-2 text-foreground/80">
            <Mail className="w-4 h-4 text-primary" /> E-mail cím*
          </label>
          <input
            type="email"
            className={`w-full rounded-xl border bg-background px-4 py-3 outline-hidden focus:ring-2 transition-all shadow-xs ${
              errors.email ? "border-red-500 focus:ring-red-500/10" : "border-border focus:ring-primary/20 focus:border-primary"
            }`}
            placeholder="nev@email.hu"
            {...register("email", {
              required: "E-mail cím kötelező",
              pattern: {
                value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
                message: "Érvénytelen e-mail cím",
              },
            })}
          />
          {errors.email && (
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">{errors.email.message}</p>
          )}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-bold flex items-center gap-2 text-foreground/80">
            <Phone className="w-4 h-4 text-primary" /> Telefonszám*
          </label>
          <input
            type="text"
            className={`w-full rounded-xl border bg-background px-4 py-3 outline-hidden focus:ring-2 transition-all shadow-xs ${
              errors.phone ? "border-red-500 focus:ring-red-500/10" : "border-border focus:ring-primary/20 focus:border-primary"
            }`}
            placeholder="+36 30 123 4567"
            {...register("phone", {
              required: "Telefonszám kötelező",
              pattern: {
                value: /^\+?[\d\s]{7,20}$/,
                message: "Érvénytelen telefonszám",
              },
            })}
          />
          {errors.phone && (
            <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">{errors.phone.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-bold flex items-center gap-2 text-foreground/80">
          <MessageSquare className="w-4 h-4 text-primary" /> Üzenet*
        </label>
        <textarea
          className={`min-h-[120px] w-full rounded-xl border bg-background px-4 py-3 outline-hidden focus:ring-2 transition-all shadow-xs resize-none ${
            errors.msg ? "border-red-500 focus:ring-red-500/10" : "border-border focus:ring-primary/20 focus:border-primary"
          }`}
          placeholder="Írja le kérdését vagy kérését..."
          {...register("msg", {
            required: "Kérjük, írjon nekünk üzenetet!",
          })}
        />
        {errors.msg && (
          <p className="text-[10px] text-red-600 font-bold uppercase tracking-wider">{errors.msg.message}</p>
        )}
      </div>

      <div className="pt-2">
        <Button
            type="submit"
            disabled={isLoading}
            className="w-full md:w-auto px-12 py-7 rounded-2xl bg-accent hover:bg-accent/90 text-accent-foreground font-black text-lg shadow-lg hover:shadow-xl transition-all group"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Küldés folyamatban...
            </div>
          ) : (
            <div className="flex items-center gap-2">
                ÜZENET ELKÜLDÉSE
                <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </div>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground italic leading-relaxed">
        * Jelölt mezők kitöltése kötelező. A gombra kattintva Ön hozzájárul adatainak kezeléséhez az adatvédelmi tájékoztatóban foglaltak szerint.
      </p>
    </form>
  );
}
